import React, {
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    Children,
    cloneElement,
    memo,
} from 'react';
import styled from 'styled-components';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { addParam } from '@one/utils';
import {
    NavigationContext,
    TOGGLE_ACCORDION,
    TOGGLE_MOBILE_DRAWER,
} from '../context/NavigationContext';
import FeatherIcon from '../util/FeatherIcon';
import { getPathToRoute } from '../util/getPathToRoute';
import getStyles from '../util/getStyles';
import AccordionContainer from './components/AccordionContainer';
import { IconComponent } from './components/IconComponent';
import { createPageViewRequest } from '../util/createPageViewRequest';
import Link from '../util/Link';

const defaultStyles = {
    button: {
        lineHeight: 1,
        display: 'flex',
        textDecoration: 'none',

        borderWidth: 0,
        borderStyle: 'solid',
        borderColor: 'transparent',
    },
    preIcon: { height: '1em', color: '#dddddd' },
    postIcon: { height: '1em', color: '#dddddd' },
    dropdown: { borderStyle: 'solid', borderColor: 'transparent', borderWidth: 0 },
    label: { display: 'flex', alignItems: 'center', textDecoration: 'none', padding: '4px 8px' },
};

const ButtonComponent = styled.div`
    display: flex;

    &:hover {
        & > a svg {
            transform: rotate(${props => props.postIconActiveRotation});
        }
    }

    &.collapsible.open {
        & > span svg {
            transform: rotate(${props => props.postIconActiveRotation});
        }
    }

    a,
    span {
        display: flex;
        flex: 1;
        align-items: center;
    }

    ${({ styles }) => styles}
`;

const Wrapper = styled.div`
    position: relative;

    .subnav {
        height: auto;
        visibility: hidden; /* display: none prevents calculation of offset */
        max-height: ${({ subnavTopPosition }) => `calc(100vh - ${subnavTopPosition}px);`}
        width: auto;
        white-space: nowrap;
        overflow: auto;
    }

    &:hover {
        .subnav {
            visibility: visible;
        }
    }

    .dynamicGroupsCollapse {
        transition: max-height 300ms;
        overflow: hidden;
    }
`;

const InitDropdown = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    min-width: auto;
    padding: 8px 0;
`;

const typeStyles = {
    left: {
        position: 'fixed',
        left: '100%',
        height: '100vh',
        right: 'auto',
        top: '0',
        minWidth: '0',
    },
    right: {
        position: 'fixed',
        right: '100%',
        height: '100vh',
        left: 'auto',
        top: '0',
    },
    bottom: {
        top: 'auto',
        bottom: '100%',
    },
};

const getDropdownStyles = navAlignment => {
    const result = {};
    if (navAlignment === 'left' || navAlignment === 'right') {
        result.top = navAlignment === 'left' ? '3em' : '1em';
        result.left = navAlignment === 'left' ? '4em' : 'auto';
        result.position = 'absolute';
        result.zIndex = 110;
        result.maxWidth = '300px';
        result.width = 'max-content';
    } else if (navAlignment === 'top' || navAlignment === 'bottom') {
        result.width = '100%';
    }
    return result;
};

const PostIcon = styled(FeatherIcon)(({ styles }) => styles);
const PreIcon = styled(FeatherIcon)(({ styles }) => styles);

const CustomPostIcon = styled(IconComponent)(({ styles }) => styles);
const CustomPreIcon = styled(IconComponent)(({ styles }) => styles);

const StyledAccordionLabel = styled.span(({ styles }) => styles);
const StyledLinkLabel = styled(Link)(({ styles }) => styles);
const StyledButtonDropdown = styled(InitDropdown)(({ styles }) => styles);

const Button = ({
    attributes,
    attributes: {
        label,
        preIcon,
        postIcon,
        path = '',
        shouldOpenNewTab,
        shouldForceRefresh,
        popunder,
        isNoFollow,
        customPath,
        postIconActiveRotation,
        labelTextRotation,
        fontSelection = '',
        autoLogin,
        autoLoginUrl,
    },
    children,
    id,
    isChildButton,
    isDynamicGroup,
    structureType,
    styles = {},
    ...props
}) => {
    const {
        dispatch,
        onHelmet,
        onFlirt4FreeLink,
        routesMap,
        state: { activeAccordionId, prevAccordionId, menuOpen },
        user: { boxToken = '' } = {},
        iconData,
        navAlignment,
        pageType,
        pageId,
        statePage,
        instanceId,
    } = useContext(NavigationContext);

    const subnavRef = useRef(null);

    const [subnavTopPosition, setSubnavTopPosition] = useState(0);

    const getTopPosition = () => {
        setSubnavTopPosition(
            subnavRef?.current?.offsetParent?.offsetTop +
                subnavRef?.current?.offsetParent?.offsetHeight,
        );
    };

    useEffect(() => {
        if (!subnavRef) return undefined;

        getTopPosition();

        // TODO: use `lodash.debounce` to increase performance by calculating the top position only after a delay
        // https://css-tricks.com/debouncing-throttling-explained-examples/
        window.addEventListener('resize', getTopPosition);

        return () => {
            window.removeEventListener('resize', getTopPosition);
        };
    }, []);

    const hasChildren = typeof children === 'object' && !isEmpty(children);
    const isMobileCollapsible = hasChildren && structureType === 'mobile';
    const isDesktopCollapsible = hasChildren && structureType === 'desktop';
    const isOpen = activeAccordionId === id || String(activeAccordionId).startsWith(id);
    const wasOpen = prevAccordionId === id;
    const isClickableButton = structureType === 'mobile' && (!hasChildren || isChildButton);
    const hasCustomIcons = !!iconData?.id;

    const {
        rootStyles = {},
        rootHoverStyles = {},
        childrenStyles = {},
    } = useMemo(() => getStyles(styles));
    const currentUrl = window.location.href;

    const buttonDropdownStyles = {
        ...defaultStyles.dropdown,
        ...childrenStyles.dropdown,
        ...(isChildButton && { position: 'relative', top: '0%' }),
        ...(navAlignment !== 'top' && !isDynamicGroup) && typeStyles[navAlignment],
        ...(!isDynamicGroup && { ...getDropdownStyles(navAlignment) }),
    };

    const accorditionStyles = {
        ...defaultStyles.dropdown,
        ...childrenStyles.dropdown,
        ...typeStyles[navAlignment],
    };

    const labelStyles = {
        ...defaultStyles.label,
        ...childrenStyles.label,
        ...(labelTextRotation && { transform: `rotate(${labelTextRotation})` }),
    };
    const Label = isMobileCollapsible ? StyledAccordionLabel : StyledLinkLabel;

    const handleClick = event => {
        const target = event.currentTarget;
        const parentElement = target?.parentElement?.parentElement;
        const parentEvent = parentElement?.classList.contains('dropdown') ? parentElement.id : '';
        createPageViewRequest({ eventName: label,
            origin: pageType,
            instanceId,
            pageId,
            statePage,
            parentEvent,
            currentUrl });
        if (isClickableButton && menuOpen) {
            onHelmet({ previous: true, next: false });
            dispatch({ type: TOGGLE_MOBILE_DRAWER });
        }
        if (isMobileCollapsible) {
            dispatch({ type: TOGGLE_ACCORDION, id });
        }

        if (shouldForceRefresh && typeof window !== 'undefined') {
            window.location.reload();
        }
        if (path === 'flirt4Free' && typeof onFlirt4FreeLink === 'function') {
            onFlirt4FreeLink({ shouldOpenNewTab });
        }
    };

    const nextChildren = children
        ? Children.map(children, child => cloneElement(child, { isChildButton: true }))
        : null;

    const postIconStyles = { ...defaultStyles.postIcon, ...childrenStyles.postIcon };
    const preIconStyles = { ...defaultStyles.preIcon, ...childrenStyles.preIcon };
    const buttonStyles = {
        ...defaultStyles.button,
        ...rootStyles,
        ...(isChildButton && hasChildren && { flexDirection: 'column' }),
        ...(isMobileCollapsible && { flexDirection: 'column', alignItems: 'stretch' }),
        ...(structureType === 'mobile' && { flexShrink: 0 }),
        '&:hover >': {
            ...rootStyles['&:hover'],
            [StyledAccordionLabel]: {
                ...rootHoverStyles.label,
                [PostIcon]: rootHoverStyles.postIcon,
                [PreIcon]: rootHoverStyles.preIcon,
                [CustomPostIcon]: rootHoverStyles.postIcon,
                [CustomPreIcon]: rootHoverStyles.preIcon,
            },
            [StyledLinkLabel]: {
                ...rootHoverStyles.label,
                [PostIcon]: rootHoverStyles.postIcon,
                [PreIcon]: rootHoverStyles.preIcon,
                [CustomPostIcon]: rootHoverStyles.postIcon,
                [CustomPreIcon]: rootHoverStyles.preIcon,
            },
        },
    };

    const buttonUrl =
        (autoLogin && addParam(autoLoginUrl, 'token', boxToken)) ||
        getPathToRoute({ routesMap, path, customPath });

    return (
        <Wrapper subnavTopPosition={subnavTopPosition} navAlignment={navAlignment}>
            <ButtonComponent
                id={`btn_${id}`}
                key={`btn_${id}`}
                onClick={handleClick}
                postIconActiveRotation={postIconActiveRotation}
                {...props}
                className={`${fontSelection} ${isMobileCollapsible ? 'collapsible' : ''} ${
                    isOpen ? 'open' : ''
                }`}
                styles={buttonStyles}
            >
                <Label
                    to={buttonUrl}
                    popunder={popunder}
                    {...(shouldOpenNewTab && { target: '_blank' })}
                    {...(isNoFollow && { rel: 'nofollow' })}
                    styles={labelStyles}
                >
                    {!hasCustomIcons ? (
                        <>
                            <PreIcon icon={preIcon} styles={preIconStyles} />
                            {label}
                            <PostIcon icon={postIcon} styles={postIconStyles} />
                        </>
                    ) : (
                        <>
                            <CustomPreIcon iconName={preIcon} styles={preIconStyles} />
                            {label}
                            <CustomPostIcon iconName={postIcon} styles={postIconStyles} />
                        </>
                    )}
                </Label>
            </ButtonComponent>
            {isMobileCollapsible && (
                <AccordionContainer
                    id={`AccordionContainer_${id}`}
                    isOpen={isOpen}
                    wasOpen={wasOpen}
                    styles={accorditionStyles}
                >
                    {nextChildren}
                </AccordionContainer>
            )}
            {isDesktopCollapsible && (
                <StyledButtonDropdown
                    styles={buttonDropdownStyles}
                    {...(!isChildButton &&
                        children.length && { className: 'subnav', ref: subnavRef })}
                >
                    {nextChildren}
                </StyledButtonDropdown>
            )}
        </Wrapper>
    );
};

Button.propTypes = {
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    styles: PropTypes.object,
    children: PropTypes.node,
    attributes: PropTypes.object,
    structureType: PropTypes.string,
    isChildButton: PropTypes.bool,
    isDynamicGroup: PropTypes.bool,
};

export default memo(Button);
